/**
 * switchStepRendererStyles.ts - Styles for SwitchStepRenderer component
 *
 * Purpose:
 *   Styles for rendering conditional branch (switch) steps in automation flows.
 *   Handles cases, filters, and nested template previews.
 *
 * Usage:
 *   import { switchStepStyles } from './styles/switchStepRendererStyles';
 *   <div className={css(switchStepStyles.container)} />
 *
 * Dependencies:
 *   - colors.ts for color palette
 *   - cardStyles.ts for card patterns
 *   - layoutConstants.ts for dimensions
 *   - branchEdgeStyles.ts for branch colors
 *
 * Related:
 *   - SwitchStepRenderer.tsx
 *   - BranchEdge.tsx
 */

import { StyleSheet } from 'aphrodite';
import { BRANCH_COLORS, DEFAULT_BRANCH_COLOR } from '../../../edges/branchEdgeStyles';
import { charmGray, white, gray3, titles } from '../../../../../styles/colors';
import { SPACING, FONT_SIZE, BORDER_RADIUS } from '../../../utils/layoutConstants';

export const styleSheet = StyleSheet.create({
	// Layout container
	container: {
		display: 'flex',
		flexDirection: 'column',
	},

	// Info section with instructions
	infoSection: {
		padding: SPACING.MD,
		border: '1px solid #E5E7EB',
		borderRadius: BORDER_RADIUS.MEDIUM,
		backgroundColor: '#FAFAFA',
		marginBottom: SPACING.MD,
	},

	infoText: {
		fontSize: FONT_SIZE.BASE,
		color: gray3,
		marginBottom: SPACING.SM,
	},

	emptyCasesText: {
		fontSize: FONT_SIZE.SM,
		color: charmGray,
		fontStyle: 'italic',
	},

	// Container for all cases
	casesContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACING.MD,
		// Removed maxHeight and overflowY to allow natural expansion without scrollbar
		// The parent expanded card will handle any necessary overflow
	},

	// Individual case card
	caseCard: {
		border: '1px solid #D1D5DB',
		borderRadius: BORDER_RADIUS.MEDIUM,
		padding: SPACING.MD,
		backgroundColor: white,
		borderLeft: '4px solid',
	},

	// Case header with badge and title
	caseHeader: {
		fontSize: FONT_SIZE.BASE,
		fontWeight: 600,
		color: titles,
		marginBottom: SPACING.SM,
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.SM,
	},

	// Case name
	caseName: {
		flex: 1,
	},

	// Editable case name styling
	caseNameEditable: {
		cursor: 'pointer',
		':hover': {
			textDecoration: 'underline',
			textDecorationStyle: 'dotted',
		},
	},

	// Case name input when editing
	caseNameInput: {
		flex: 1,
		fontSize: FONT_SIZE.BASE,
		fontWeight: 600,
		padding: '2px 4px',
	},

	// Case node count badge
	caseNodeCount: {
		fontSize: FONT_SIZE.SM,
		color: charmGray,
		fontWeight: 400,
		marginLeft: 'auto',
	},

	// Badge styling
	caseBadge: {
		padding: '2px 8px',
		borderRadius: BORDER_RADIUS.SMALL,
		fontSize: FONT_SIZE.SM,
		fontWeight: 600,
		color: white,
	},

	caseBadgeNormal: {
		backgroundColor: '#DBEAFE',
		color: '#1E40AF',
	},

	caseBadgeDefault: {
		backgroundColor: '#F3F4F6',
		color: charmGray,
	},

	// Template preview container
	templatePreviewContainer: {
		marginTop: SPACING.MD,
	},

	templatePreviewContainerWithFilter: {
		marginTop: SPACING.MD,
	},

	// Loading state
	loadingContainer: {
		padding: SPACING.XL,
		textAlign: 'center',
		marginTop: SPACING.MD,
	},

	loadingContainerWithFilter: {
		padding: SPACING.XL,
		textAlign: 'center',
		marginTop: SPACING.MD,
	},

	// No template message
	noTemplateText: {
		fontSize: FONT_SIZE.SM,
		color: charmGray,
		fontStyle: 'italic',
		marginTop: SPACING.MD,
	},

	noTemplateTextWithFilter: {
		fontSize: FONT_SIZE.SM,
		color: charmGray,
		fontStyle: 'italic',
		marginTop: SPACING.MD,
	},
});

// Backwards compatible alias
export const switchStepStyles = styleSheet;

/**
 * Gets the border color for a case based on its index
 */
export function getCaseBorderColor(caseIndex: number, isDefault?: boolean): string {
	if (isDefault) {
		return DEFAULT_BRANCH_COLOR;
	}
	return BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
}
